/**
 * 系统公告相关 Hooks
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  type Announcement,
  getMarquee,
  getPopups,
  markAsRead,
} from '@/services/announcementService';

/**
 * 跑马灯公告 Hook
 * 获取跑马灯公告列表，支持定时刷新
 */
export function useMarquee(refreshInterval?: number) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMarquee = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMarquee(signal);
      setAnnouncements(data || []);
    } catch (err) {
      // 忽略取消请求的错误
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err : new Error('获取公告失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchMarquee();
  }, [fetchMarquee]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchMarquee(abortController.signal);

    // 设置定时刷新（如果配置了刷新间隔）
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (refreshInterval && refreshInterval > 0) {
      intervalId = setInterval(() => {
        fetchMarquee();
      }, refreshInterval);
    }

    return () => {
      abortController.abort();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchMarquee, refreshInterval]);

  return { announcements, loading, error, refresh };
}

/**
 * 弹窗公告 Hook
 * 获取未读弹窗公告列表，支持逐条显示和标记已读
 * @param enabled 是否启用（默认 true），设为 false 时不会请求接口
 */
export function usePopupAnnouncements(enabled: boolean = true) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  // 使用 ref 跟踪是否已经加载过
  const hasLoaded = useRef(false);

  const fetchPopups = useCallback(async (signal?: AbortSignal) => {
    // 如果已经加载过，不再重复加载
    if (hasLoaded.current) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getPopups(signal);
      setAnnouncements(data || []);
      setCurrentIndex(0);
      hasLoaded.current = true;
    } catch (err) {
      // 忽略取消请求的错误
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err : new Error('获取公告失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  // 标记当前公告为已读并显示下一条
  const markCurrentAsRead = useCallback(async () => {
    const current = announcements[currentIndex];
    if (!current) return;

    try {
      await markAsRead(current.id);
    } catch (err) {
      // 标记已读失败不影响用户继续查看
      console.error('标记公告已读失败:', err);
    }

    // 移动到下一条或关闭
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      // 所有公告都已查看，清空列表
      setAnnouncements([]);
      setCurrentIndex(0);
    }
  }, [announcements, currentIndex]);

  // 跳过当前公告（不标记已读）
  const skipCurrent = useCallback(() => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setAnnouncements([]);
      setCurrentIndex(0);
    }
  }, [announcements.length, currentIndex]);

  // 关闭所有公告
  const closeAll = useCallback(() => {
    setAnnouncements([]);
    setCurrentIndex(0);
  }, []);

  // 当前显示的公告
  const currentAnnouncement = announcements[currentIndex] || null;

  // 是否有公告需要显示
  const hasAnnouncements = announcements.length > 0;

  // 剩余公告数量
  const remainingCount = Math.max(0, announcements.length - currentIndex);

  useEffect(() => {
    // 未启用时不请求
    if (!enabled) {
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    fetchPopups(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchPopups, enabled]);

  return {
    announcements,
    loading,
    error,
    currentIndex,
    currentAnnouncement,
    hasAnnouncements,
    remainingCount,
    markCurrentAsRead,
    skipCurrent,
    closeAll,
    refresh: fetchPopups,
  };
}
